import { Controller } from "stimulus"
import { loadStripe } from "@stripe/stripe-js"

// Owns every Stripe.js provider mechanic behind one seam: script loading,
// Payment Element mount/unmount, submission, confirmation/3DS, and safe error
// classification. Product controllers never talk to Stripe.js directly.
//
// Two modes share the provider mechanics:
//
// 1. Legacy immediate mode (Guest Posts, Return Policy): the element mounts on
//    connect with a deferred-intent amount and `submitPayment` fetches the
//    PaymentIntent itself. Unchanged behavior.
//
//    data-controller="stripe" data-stripe-stripe-key-value="pk_..."
//    data-stripe-amount-value / -payment-intent-url-value / -url-value ...
//
// 2. Deferred mode (Bidwise Card Funding): another controller owns the intent
//    lifecycle; this controller answers with provider outcome events and never
//    touches product state. Two mount variants share the confirm path:
//
//    - "stripe:mount-amount" {amount} — deferred-intent amount mode: the card
//      form renders before any PaymentIntent exists; "stripe:update-amount"
//      {amount} keeps the element in sync with the product amount input, and
//      "stripe:submit" validates the card form (elements.submit()) before the
//      product controller creates the intent. The transient client secret then
//      arrives late, in the "stripe:confirm" event detail.
//    - "stripe:mount" {clientSecret} — binds the element to one existing
//      PaymentIntent's transient client secret (reload/3DS recovery).
//
//    data-controller="stripe" data-stripe-deferred-value="true"
//    data-stripe-disable-wallets-value="true" data-stripe-url-value="<return_url>"
//    data-action="stripe:mount->stripe#mountDeferred stripe:mount-amount->stripe#mountAmount
//                 stripe:update-amount->stripe#updateAmount stripe:submit->stripe#submitDeferred
//                 stripe:confirm->stripe#confirmDeferred"
//
//    Emits bubbling events: "stripe:ready" (element interactive),
//    "stripe:error" {message} (setup/load failure, retryable),
//    "stripe:submit-ready" (card form validated, safe to create the intent),
//    "stripe:confirm-error" {message} (immediate definitive confirmation or card
//    validation error; the same PaymentIntent stays chargeable and may be retried),
//    "stripe:confirm-submitted" (confirmation accepted without immediate error —
//    processing, never success).
export default class extends Controller {
  static targets = [ 'container', 'messageError', 'loader', 'messagePaymentProcessed', 'payButton', 'dataForm', 'email', 'inputParam' ]
  static values = { stripeKey: String, paymentIntentUrl: String, paymentIntentParameters: Object, amount: Number, email: String, url: String, redirect: Boolean, deferred: Boolean, disableWallets: Boolean }

  async connect() {
    // Loading can fail (offline, blocked script); deferred consumers get an
    // event to show retryable copy, the legacy flow keeps its old behavior of
    // failing on first use.
    try {
      this.stripe = await loadStripe(this.stripeKeyValue);
    } catch (error) {
      this.stripe = null
    }

    if (this.deferredValue) {
      // The element mounts only when a client secret arrives via "stripe:mount";
      // a failed script load surfaces there, when someone is actually watching.
      return
    }

    const options = {
      mode: 'payment',
      amount: this.amountValue,
      currency: 'usd',
      appearance: this.appearance(),
    };

    this.stripeElements = this.stripe.elements(options)
    this.paymentElement = this.stripeElements.create('payment', this.paymentElementOptions())
    this.mount()
  }

  disconnect() {
    this.unmount()
  }

  unmount() {
    if (this.paymentElement) {
      this.paymentElement.unmount()
    }
  }

  mount() {
    if (this.paymentElement) {
      this.paymentElement.mount(this.containerTarget)
    }
  }

  // Deferred mode: binds the Payment Element to one existing PaymentIntent's
  // transient client secret (event detail `clientSecret`). Remounting with the
  // same secret is a no-op re-announce so recovery paths stay idempotent.
  async mountDeferred(event) {
    const clientSecret = event.detail && event.detail.clientSecret

    if (!this.stripe || !clientSecret) {
      this.emit('stripe:error', { message: 'Secure payment could not be loaded. Please reload the page and try again.' })
      return
    }

    if (this.mountedClientSecret === clientSecret) {
      this.emit('stripe:ready')
      return
    }

    this.unmount()
    this.mountedClientSecret = clientSecret
    this.stripeElements = this.stripe.elements({ clientSecret: clientSecret, appearance: this.appearance() })
    this.paymentElement = this.stripeElements.create('payment', this.paymentElementOptions())
    this.paymentElement.on('ready', () => this.emit('stripe:ready'))
    this.paymentElement.mount(this.containerTarget)
  }

  // Deferred-amount mode: mounts the Payment Element before any PaymentIntent
  // exists, bound only to an amount in cents (event detail `amount`). Card-only
  // is explicit here because there is no intent yet to constrain method types.
  async mountAmount(event) {
    const amount = event.detail && event.detail.amount

    if (!this.stripe || !amount) {
      this.emit('stripe:error', { message: 'Secure payment could not be loaded. Please reload the page and try again.' })
      return
    }

    this.unmount()
    this.mountedClientSecret = null
    this.stripeElements = this.stripe.elements({
      mode: 'payment',
      amount: amount,
      currency: 'usd',
      paymentMethodTypes: [ 'card' ],
      appearance: this.appearance()
    })
    this.paymentElement = this.stripeElements.create('payment', this.paymentElementOptions())
    this.paymentElement.on('ready', () => this.emit('stripe:ready'))
    this.paymentElement.mount(this.containerTarget)
  }

  // Deferred-amount mode: keeps the mounted element's amount (cents) in sync
  // with the product amount input. Ignored once the element is bound to a
  // client secret, whose PaymentIntent already owns the amount.
  updateAmount(event) {
    const amount = event.detail && event.detail.amount

    if (this.stripeElements && !this.mountedClientSecret && amount) {
      this.stripeElements.update({ amount: amount })
    }
  }

  // Deferred-amount mode: validates the card form (elements.submit()) BEFORE
  // the product controller creates a PaymentIntent, so invalid card details
  // never cause a chargeable intent. Emits "stripe:submit-ready" on success;
  // validation failures reuse "stripe:confirm-error" so product error handling
  // stays in one place.
  async submitDeferred() {
    if (!this.stripeElements || this.confirming) {
      return
    }

    try {
      const { error } = await this.stripeElements.submit()

      if (error) {
        this.emit('stripe:confirm-error', { message: this.safeErrorMessage(error) })
      } else {
        this.emit('stripe:submit-ready')
      }
    } catch (error) {
      this.emit('stripe:confirm-error', { message: this.genericErrorMessage() })
    }
  }

  // Deferred mode: confirms the PaymentIntent. Amount-mounted elements have no
  // intent baked in, so the transient client secret arrives in the event detail;
  // secret-mounted elements already carry it. 3DS renders inside the Element or
  // returns the browser to the server-owned return URL; a resolved call without
  // error only means the confirmation was submitted.
  async confirmDeferred(event) {
    if (!this.stripeElements || this.confirming) {
      return
    }

    const args = {
      elements: this.stripeElements,
      confirmParams: { return_url: this.urlValue },
      redirect: 'if_required'
    }
    // Late client secret only applies to amount-mode elements; passing one
    // alongside secret-mounted elements would double-bind the intent.
    const clientSecret = event && event.detail && event.detail.clientSecret
    if (clientSecret && !this.mountedClientSecret) {
      args.clientSecret = clientSecret
    }

    this.confirming = true
    try {
      const response = await this.stripe.confirmPayment(args)

      if (response.error) {
        this.emit('stripe:confirm-error', { message: this.safeErrorMessage(response.error) })
      } else {
        this.emit('stripe:confirm-submitted')
      }
    } catch (error) {
      // A thrown confirmation proves nothing about the charge; report it as
      // retryable and let authoritative polling decide the real outcome.
      this.emit('stripe:confirm-error', { message: this.genericErrorMessage() })
    } finally {
      this.confirming = false
    }
  }

  async submitPayment() {
    if (this.hasDataFormTarget) {
      let isValid = this.validateInputs()

      if (!isValid) {
        return
      }

      if (this.dataFormTarget instanceof HTMLFormElement) {
        this.dataFormTarget.submit()
      }
    }

    // Make sure there are no validation errors on the credit card form
    const {error: submitError} = await this.stripeElements.submit();

    if (submitError) {
      this.showError(submitError.message)
      return
    }

    if (this.messageErrorTarget.classList.contains('visible')) {
      this.messageErrorTarget.classList.remove('visible')
    }

    this.messagePaymentProcessedTarget.classList.add('visible')
    this.loaderTarget.classList.add('visible')
    this.payButtonTarget.disabled = true

    let clientSecret
    try {
      const res = await fetch(this.paymentIntentUrlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": this.csrfToken()
        },
        // Merge any paymentIntentParameters specified with any other "inputParam" values
        body: JSON.stringify({ ...this.paymentIntentParametersValue, ...this.inputParams })
      })

      // A non-2xx or non-JSON intent response has no client secret; surface a
      // retryable message instead of confirming against undefined.
      if (!res.ok) {
        this.showError(this.genericErrorMessage())
        return
      }

      ({client_secret: clientSecret} = await res.json())
    } catch (error) {
      this.showError(this.genericErrorMessage())
      return
    }

    if (!clientSecret) {
      this.showError(this.genericErrorMessage())
      return
    }

    const url = this.urlValue
    const email = this.hasEmailValue ? this.emailValue : this.emailTarget.value

    let args = {
      elements: this.stripeElements,
      clientSecret: clientSecret,
      confirmParams: {
        return_url: url,
        payment_method_data: {
          billing_details: {
            email: email
          }
        }
      },
      redirect: 'if_required'
    }

    const response = await this.stripe.confirmPayment(args);

    if (response.error) {
      this.showError(response.error.message)
    }
    else {
      window.dispatchEvent(new CustomEvent('submit-payment-success'));
      if (this.hasRedirectValue && this.redirectValue === true) {
        window.location.replace(url)
      }
    }
  }

  showError(errorMessage) {
    this.messageErrorTarget.innerText = errorMessage
    this.messageErrorTarget.classList.add('visible')

    if (this.messagePaymentProcessedTarget.classList.contains('visible')) {
      this.messagePaymentProcessedTarget.classList.remove('visible')
    }

    if (this.loaderTarget.classList.contains('visible')) {
      this.loaderTarget.classList.remove('visible')
    }

    if (this.payButtonTarget.disabled === true) {
      this.payButtonTarget.disabled = false
    }
  }

  validateInputs() {
    let isValid = true

    this.inputs.reverse().forEach((input) => {
      if (!input.checkValidity()) {
        input.reportValidity()
        isValid = false
      }
    })

    return isValid
  }

  get inputs() {
    return Array.from(this.dataFormTarget.querySelectorAll('input'))
  }

  get inputParams() {
    const params = {}
    this.inputParamTargets.forEach((element) => {
      params[element.name] = element.value
    })
    return params
  }

  appearance() {
    return {
      theme: 'stripe',

      variables: {
        fontFamily: '--body-font-family: "Open Sans", sans-serif',
        fontSizeSm: '16px',
        spacingUnit: '5px',
        borderRadius: '30px',
        fontWeightMedium: '500',
      },

      rules: {
        '.Label': {
          fontWeight: 'var(--fontWeightMedium)',
          marginTop: '14px'
        }
      }
    };
  }

  // Wallet exclusion is explicit so eligible test/live browsers never surface
  // Apple Pay or Google Pay UI; card-only method types come from the intent.
  paymentElementOptions() {
    if (this.disableWalletsValue) {
      return { wallets: { applePay: 'never', googlePay: 'never' } }
    }

    return {}
  }

  // Card and validation errors carry Stripe's actionable client-safe message;
  // anything else collapses to a generic retryable message so provider
  // internals never leak into the page.
  safeErrorMessage(error) {
    if (error.type === 'card_error' || error.type === 'validation_error') {
      return error.message
    }

    return this.genericErrorMessage()
  }

  genericErrorMessage() {
    return 'We could not confirm your payment. Please check your details and try again.'
  }

  csrfToken() {
    const meta = document.querySelector('meta[name="csrf-token"]')
    return meta ? meta.content : ''
  }

  emit(name, detail = {}) {
    this.element.dispatchEvent(new CustomEvent(name, { bubbles: true, detail: detail }))
  }
}
